// Which right-side properties a one-line list row hides behind its "+N" pill when the row is too narrow (WBS-13010).

/** Fold order, least important first; properties not named here fold before all of these. */
export const LIST_ROW_V2_FOLD_ORDER = ['updatedAt', 'updated', 'createdAt', 'created', 'type', 'tags', 'target', 'dueDate', 'due'];

/** One foldable property as measured while every property was shown. */
export interface ListRowV2FoldItem {
  id: string;
  width: number;
}

/** Measured row space the fold has to fit into. */
export interface ListRowV2FoldSpace {
  /** Width the right side may use once the title keeps its floor. */
  budget: number;
  /** Combined width of the properties that never fold (evidence, assignee). */
  pinnedWidth: number;
  gap: number;
  /** Width of the "+N" pill, reserved as soon as one property folds. */
  overflowWidth: number;
}

/**
 * Called by the v2 list row after each measure or resize. Returns the ids to hide, folding in
 * LIST_ROW_V2_FOLD_ORDER until the rest fits; never overlaps or squeezes a property, so a chip is
 * either whole or behind "+N".
 */
export function foldListRowProperties(items: readonly ListRowV2FoldItem[], space: ListRowV2FoldSpace): Set<string> {

  const folded = new Set<string>();
  const order = [...items].sort((a, b) => foldRank(a.id) - foldRank(b.id));

  for (const item of order) {
    if (requiredWidth(items, folded, space) <= space.budget) break;
    folded.add(item.id);
  }

  return folded;
}

function foldRank(id: string): number {

  return LIST_ROW_V2_FOLD_ORDER.indexOf(id);
}

function requiredWidth(items: readonly ListRowV2FoldItem[], folded: ReadonlySet<string>, space: ListRowV2FoldSpace): number {

  const shown = items.filter(item => !folded.has(item.id));
  const widths = shown.map(item => item.width);
  if (folded.size) widths.push(space.overflowWidth);
  if (space.pinnedWidth > 0) widths.push(space.pinnedWidth);

  return widths.reduce((sum, width) => sum + width, 0) + Math.max(0, widths.length - 1) * space.gap;
}
